/**
 * Workflow Details Sidebar
 * 
 * Shows workflow details in a right sidebar with inline editing.
 * Save/Cancel buttons only activate when changes are made.
 */

import React, { useState, useEffect } from 'react';


// Widget type compatibility matrix
const WIDGET_COMPATIBILITY: Record<string, string[]> = {
  'string': ['text', 'textarea', 'select', 'aspect-ratio', 'rich-dropdown', 'color', 'folder', 'image', 'document'],
  'integer': ['number', 'slider', 'seed', 'select', 'resolution-width'],
  'number': ['number', 'slider', 'select', 'resolution-width'],
  'boolean': ['checkbox', 'toggle'],
  'array': ['images', 'documents', 'select'],
};

// Group types
type GroupType = 'basic' | 'advanced';

// Widget type display names
const WIDGET_DISPLAY_NAMES: Record<string, string> = {
  'text': 'Text Input',
  'textarea': 'Text Area',
  'number': 'Number Input',
  'slider': 'Slider',
  'seed': 'Seed (with randomize)',
  'select': 'Dropdown Select',
  'rich-dropdown': 'Rich Dropdown (with thumbnails)',
  'aspect-ratio': 'Aspect Ratio Selector',
  'resolution-width': 'Resolution Dropdown',
  'checkbox': 'Checkbox',
  'toggle': 'Toggle Switch',
  'image': 'Image Upload',
  'images': 'Multiple Images',
  'folder': 'Folder Picker',
  'document': 'Document Upload',
  'documents': 'Multiple Documents',
  'color': 'Color Picker',
};

interface WorkflowDetailsSidebarProps {
  workflowId: string;
  tenantId: string;
  apiBaseUrl: string;
  authToken: string;
  onClose: () => void;
  onUpdate: () => void;
}

export const WorkflowDetailsSidebar: React.FC<WorkflowDetailsSidebarProps> = ({
  workflowId,
  tenantId,
  apiBaseUrl,
  authToken,
  onClose,
  onUpdate
}) => {
  const [workflow, setWorkflow] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editable fields
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [icon, setIcon] = useState('');
  const [jsonSchema, setJsonSchema] = useState('');
  const [uiSchema, setUiSchema] = useState('');
  const [parsedJsonSchema, setParsedJsonSchema] = useState<any>(null);
  const [parsedUiSchema, setParsedUiSchema] = useState<any>(null);

  // Original values for change detection
  const [originalValues, setOriginalValues] = useState<any>(null);

  // Icon upload state
  const [iconUploading, setIconUploading] = useState(false);
  const iconInputRef = React.useRef<HTMLInputElement>(null);

  // Drag and drop state
  const [draggedField, setDraggedField] = useState<string | null>(null);
  const [dragOverGroup, setDragOverGroup] = useState<GroupType | null>(null);

  useEffect(() => {
    fetchWorkflow();
  }, [workflowId]);

  const fetchWorkflow = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiBaseUrl}/api/workflows/${workflowId}?tenantId=${encodeURIComponent(tenantId)}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch workflow: ${response.statusText}`);
      }

      const data = await response.json();
      setWorkflow(data);

      // Set editable fields
      const metadata = data.metadata;
      const pkg = data.package;

      setDisplayName(metadata.displayName || '');
      setDescription(metadata.description || '');
      setTags(JSON.parse(metadata.tags || '[]').join(', '));
      setIcon(metadata.icon || '');
      setJsonSchema(JSON.stringify(pkg.schemas.jsonSchema, null, 2));
      setUiSchema(JSON.stringify(pkg.schemas.uiSchema, null, 2));
      setParsedJsonSchema(pkg.schemas.jsonSchema);
      setParsedUiSchema(pkg.schemas.uiSchema);

      // Store original values
      setOriginalValues({
        displayName: metadata.displayName || '',
        description: metadata.description || '',
        tags: JSON.parse(metadata.tags || '[]').join(', '),
        icon: metadata.icon || '',
        jsonSchema: JSON.stringify(pkg.schemas.jsonSchema, null, 2),
        uiSchema: JSON.stringify(pkg.schemas.uiSchema, null, 2)
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Check if any field has changed
  const hasChanges = () => {
    if (!originalValues) return false;
    return (
      displayName !== originalValues.displayName ||
      description !== originalValues.description ||
      tags !== originalValues.tags ||
      icon !== originalValues.icon ||
      jsonSchema !== originalValues.jsonSchema ||
      uiSchema !== originalValues.uiSchema
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      // Update metadata if changed
      if (
        displayName !== originalValues.displayName ||
        description !== originalValues.description ||
        tags !== originalValues.tags ||
        icon !== originalValues.icon
      ) {
        const tagsArray = tags.split(',').map(t => t.trim()).filter(t => t);

        const metadataResponse = await fetch(`${apiBaseUrl}/api/workflows/${workflowId}/metadata?tenantId=${encodeURIComponent(tenantId)}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            displayName,
            description,
            tags: tagsArray,
            icon
          })
        });

        if (!metadataResponse.ok) {
          const data = await metadataResponse.json();
          throw new Error(data.error || 'Failed to update metadata');
        }
      }

      // Update presentation if changed
      if (
        jsonSchema !== originalValues.jsonSchema ||
        uiSchema !== originalValues.uiSchema
      ) {
        const parsedJsonSchema = JSON.parse(jsonSchema);
        const parsedUiSchema = JSON.parse(uiSchema);

        const presentationResponse = await fetch(`${apiBaseUrl}/api/workflows/${workflowId}/presentation?tenantId=${encodeURIComponent(tenantId)}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            jsonSchema: parsedJsonSchema,
            uiSchema: parsedUiSchema
          })
        });

        if (!presentationResponse.ok) {
          const data = await presentationResponse.json();
          throw new Error(data.error || 'Failed to update presentation');
        }
      }

      alert('Workflow updated successfully!');
      onUpdate();
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges()) {
      if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const handleIconUpload = async (file: File) => {
    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      alert('Invalid file type. Please upload PNG, JPG, or SVG files only.');
      return;
    }

    // Validate file size (max 500KB)
    const maxSizeKB = 500;
    const fileSizeKB = file.size / 1024;
    if (fileSizeKB > maxSizeKB) {
      alert(`File too large. Maximum size: ${maxSizeKB}KB (current: ${Math.round(fileSizeKB)}KB)`);
      return;
    }

    setIconUploading(true);
    try {
      // Convert file to data URL first
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });

      // Upload to blob storage via API
      const response = await fetch(`${apiBaseUrl}/api/upload/file`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          dataUrl,
          filename: file.name
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upload icon');
      }

      const result = await response.json();
      // Store the blob URL instead of the data URL
      setIcon(result.blobUrl);
      setIconUploading(false);
    } catch (err: any) {
      console.error('Error uploading icon:', err);
      alert(err.message || 'Failed to upload icon');
      setIconUploading(false);
    }
  };

  const handleIconClick = () => {
    iconInputRef.current?.click();
  };

  const handleIconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleIconUpload(file);
    }
  };

  const handleIconRemove = () => {
    setIcon('');
    if (iconInputRef.current) {
      iconInputRef.current.value = '';
    }
  };

  // Handle widget type change for a specific field
  const handleWidgetTypeChange = (fieldName: string, newWidgetType: string) => {
    if (!parsedUiSchema || !parsedUiSchema.elements) return;

    // Auto-detect height field for resolution widget
    let resolutionPair: string | undefined;
    if (newWidgetType === 'resolution-width' && parsedJsonSchema?.properties) {
      // Try to find the corresponding height field
      const fieldLower = fieldName.toLowerCase();
      const possibleHeightFields = Object.keys(parsedJsonSchema.properties).filter(key => {
        const keyLower = key.toLowerCase();
        // Match patterns like: SetWidth -> SetHeight, width -> height
        return (keyLower.includes('height') || keyLower.endsWith('setheight')) &&
               (fieldLower.includes('width') || fieldLower.endsWith('setwidth'));
      });
      
      if (possibleHeightFields.length > 0) {
        resolutionPair = possibleHeightFields[0];
      }
    }

    // Find the element in UI schema
    const updatedElements = parsedUiSchema.elements.map((element: any) => {
      if (element.scope === `#/properties/${fieldName}`) {
        const newOptions: any = {
          ...element.options,
          widget: newWidgetType
        };
        
        // Add resolutionPair if detected
        if (resolutionPair) {
          newOptions.resolutionPair = resolutionPair;
        }
        
        return {
          ...element,
          options: newOptions
        };
      }
      return element;
    });

    const updatedUiSchema = {
      ...parsedUiSchema,
      elements: updatedElements
    };

    setParsedUiSchema(updatedUiSchema);
    setUiSchema(JSON.stringify(updatedUiSchema, null, 2));
  };

  // Handle constraint change (min/max/multipleOf) for a specific field
  const handleConstraintChange = (fieldName: string, constraint: 'minimum' | 'maximum' | 'multipleOf', value: string) => {
    if (!parsedJsonSchema || !parsedJsonSchema.properties) return;

    const numValue = value === '' ? undefined : Number(value);

    const updatedProperties = {
      ...parsedJsonSchema.properties,
      [fieldName]: {
        ...parsedJsonSchema.properties[fieldName],
        [constraint]: numValue
      }
    };

    // Remove undefined values
    if (numValue === undefined) {
      delete updatedProperties[fieldName][constraint];
    }

    const updatedJsonSchema = {
      ...parsedJsonSchema,
      properties: updatedProperties
    };

    setParsedJsonSchema(updatedJsonSchema);
    setJsonSchema(JSON.stringify(updatedJsonSchema, null, 2));
  };

  // Get current widget type for a field
  const getCurrentWidgetType = (fieldName: string): string => {
    if (!parsedUiSchema || !parsedUiSchema.elements) return 'text';

    const element = parsedUiSchema.elements.find((el: any) =>
      el.scope === `#/properties/${fieldName}`
    );

    return element?.options?.widget || 'text';
  };

  // Get compatible widget types for a field based on its JSON Schema type
  const getCompatibleWidgets = (fieldSchema: any): string[] => {
    const schemaType = fieldSchema.type || 'string';
    return WIDGET_COMPATIBILITY[schemaType] || ['text'];
  };

  // Check if a field is in the advanced group
  const isFieldInAdvancedGroup = (fieldName: string): boolean => {
    if (!parsedUiSchema || !parsedUiSchema.elements) return false;

    // Find the Advanced Settings group
    const advancedGroup = parsedUiSchema.elements.find((el: any) =>
      el.type === 'Group' && el.label === 'Advanced Settings'
    );

    if (!advancedGroup || !advancedGroup.elements) return false;

    // Check if field is in the advanced group
    return advancedGroup.elements.some((el: any) =>
      el.scope === `#/properties/${fieldName}`
    );
  };

  // Move field between basic and advanced groups
  const moveFieldToGroup = (fieldName: string, targetGroup: GroupType) => {
    if (!parsedUiSchema || !parsedUiSchema.elements) return;

    const isCurrentlyAdvanced = isFieldInAdvancedGroup(fieldName);
    const shouldBeAdvanced = targetGroup === 'advanced';

    // No change needed
    if (isCurrentlyAdvanced === shouldBeAdvanced) return;

    // Find or create the Advanced Settings group
    let advancedGroupIndex = parsedUiSchema.elements.findIndex((el: any) =>
      el.type === 'Group' && el.label === 'Advanced Settings'
    );

    let advancedGroup: any;
    if (advancedGroupIndex === -1) {
      // Create advanced group if it doesn't exist
      advancedGroup = {
        type: 'Group',
        label: 'Advanced Settings',
        options: {
          collapsible: true,
          collapsed: true
        },
        elements: []
      };
      advancedGroupIndex = parsedUiSchema.elements.length;
    } else {
      advancedGroup = { ...parsedUiSchema.elements[advancedGroupIndex] };
    }

    // Find the field element
    const fieldElement = parsedUiSchema.elements.find((el: any) =>
      el.scope === `#/properties/${fieldName}`
    ) || advancedGroup.elements?.find((el: any) =>
      el.scope === `#/properties/${fieldName}`
    );

    if (!fieldElement) return;

    // Remove field from advanced group if it's there
    if (advancedGroup.elements) {
      advancedGroup.elements = advancedGroup.elements.filter((el: any) =>
        el.scope !== `#/properties/${fieldName}`
      );
    }

    // Build the new elements array, excluding the field from root level and replacing the advanced group
    let updatedElements = parsedUiSchema.elements
      .filter((el: any) => el.scope !== `#/properties/${fieldName}`) // Remove field from root if present
      .map((el: any) => {
        // Replace the advanced group with our modified copy
        if (el.type === 'Group' && el.label === 'Advanced Settings') {
          return advancedGroup;
        }
        return el;
      });

    // Add field to target location
    if (shouldBeAdvanced) {
      // Add to advanced group
      if (!advancedGroup.elements) {
        advancedGroup.elements = [];
      }
      advancedGroup.elements.push(fieldElement);

      // If advanced group doesn't exist in updatedElements, add it
      const hasAdvancedGroup = updatedElements.some((el: any) =>
        el.type === 'Group' && el.label === 'Advanced Settings'
      );
      if (!hasAdvancedGroup) {
        updatedElements.push(advancedGroup);
      }
    } else {
      // Add to basic (top-level) elements
      // Find where advanced group is (if it exists) to insert before it
      const advGroupIdx = updatedElements.findIndex((el: any) =>
        el.type === 'Group' && el.label === 'Advanced Settings'
      );

      if (advGroupIdx !== -1) {
        updatedElements.splice(advGroupIdx, 0, fieldElement);
      } else {
        updatedElements.push(fieldElement);
      }

      // Remove advanced group if it's now empty
      updatedElements = updatedElements.filter((el: any) => {
        if (el.type === 'Group' && el.label === 'Advanced Settings') {
          return el.elements && el.elements.length > 0;
        }
        return true;
      });
    }

    const updatedUiSchema = {
      ...parsedUiSchema,
      elements: updatedElements
    };

    setParsedUiSchema(updatedUiSchema);
    setUiSchema(JSON.stringify(updatedUiSchema, null, 2));
  };

  // Drag and drop handlers
  const handleDragStart = (fieldName: string) => {
    setDraggedField(fieldName);
  };

  const handleDragOver = (e: React.DragEvent, group: GroupType) => {
    e.preventDefault();
    setDragOverGroup(group);
  };

  const handleDragLeave = () => {
    setDragOverGroup(null);
  };

  const handleDrop = (e: React.DragEvent, targetGroup: GroupType) => {
    e.preventDefault();
    if (draggedField) {
      moveFieldToGroup(draggedField, targetGroup);
    }
    setDraggedField(null);
    setDragOverGroup(null);
  };

  const overlay   = 'fixed inset-0 z-50 flex';
  const sidePanel = 'w-[520px] flex-shrink-0 bg-white shadow-xl flex flex-col h-full';
  const hdr       = 'flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0';
  const closeX    = 'w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors duration-fast text-lg';
  const secBtn    = 'px-4 py-2.5 bg-white border border-gray-300 text-label font-medium text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-fast disabled:opacity-50';
  const priBtn    = 'flex-1 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-label font-semibold rounded-md transition-colors duration-fast disabled:opacity-50 disabled:cursor-not-allowed';
  const inputCls  = 'w-full px-3 py-2.5 border border-gray-300 rounded-md text-body bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast';
  const tapCls    = `${inputCls} resize-y`;
  const monoCls   = `${tapCls} font-mono text-meta`;
  const lbl       = 'block text-label font-medium text-gray-700 mb-1';
  const readonlyCls = 'px-3 py-2 bg-surface-subtle border border-gray-200 rounded-md text-label text-gray-700';
  const selCls    = `${inputCls} select-input`;
  const numCls    = `${inputCls} [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`;

  if (loading) return (
    <div className={overlay}><div className="flex-1 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className={sidePanel}><div className={hdr}><span className="text-body text-gray-400">Loading workflow details…</span><button type="button" onClick={onClose} className={closeX}>×</button></div></div>
    </div>
  );

  if (error && !workflow) return (
    <div className={overlay}><div className="flex-1 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className={sidePanel}>
        <div className={hdr}><span className="text-body text-error">Error: {error}</span><button type="button" onClick={onClose} className={closeX}>×</button></div>
        <div className="px-6 py-4"><button type="button" onClick={onClose} className={secBtn}>Close</button></div>
      </div>
    </div>
  );

  if (!workflow) return null;

  const { metadata, package: pkg } = workflow;
  const parsedTags = JSON.parse(metadata.tags || '[]');
  const changesDetected = hasChanges();

  /** Helper: renders an editable variable card */
  const renderVariableCard = (key: string, prop: any, idPrefix: string) => {
    const currentWidget = getCurrentWidgetType(key);
    const compatibleWidgets = getCompatibleWidgets(prop);
    const isNumeric = prop.type === 'integer' || prop.type === 'number';
    return (
      <div key={key}
        className={`border border-gray-200 rounded-lg p-3 bg-white transition-shadow duration-fast ${draggedField === key ? 'opacity-50 shadow-lg' : 'shadow-xs hover:shadow-sm'}`}
        draggable onDragStart={() => handleDragStart(key)}>
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-2">
            <span className="text-gray-400 cursor-grab select-none">⋮⋮</span>
            <span className="text-label font-semibold text-gray-900">{prop.title || key}</span>
          </div>
          <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-meta font-mono">{prop.type}</span>
        </div>
        <div className="flex items-center gap-2 mb-2">
          <label htmlFor={`${idPrefix}-${key}`} className="text-meta font-medium text-gray-500 whitespace-nowrap">Widget:</label>
          <select id={`${idPrefix}-${key}`} value={currentWidget}
            onChange={e => handleWidgetTypeChange(key, e.target.value)}
            className="flex-1 px-2 py-1.5 border border-gray-300 rounded-md text-label bg-white outline-none focus:border-blue-400 select-input transition-colors duration-fast">
            {compatibleWidgets.map(wt => <option key={wt} value={wt}>{WIDGET_DISPLAY_NAMES[wt] || wt}</option>)}
          </select>
        </div>
        {isNumeric && (
          <div className="flex flex-col gap-1.5 mt-2 pt-2 border-t border-gray-100">
            <div className="grid grid-cols-3 gap-2">
              {([{id:`min-${idPrefix}-${key}`,field:'minimum' as const,lbl:'Min',ph:'No min'},
                {id:`max-${idPrefix}-${key}`,field:'maximum' as const,lbl:'Max',ph:'No max'},
                {id:`step-${idPrefix}-${key}`,field:'multipleOf' as const,lbl:'Step',ph:prop.type==='integer'?'1':'0.1'}
              ] as const).map(({id,field,lbl:fl,ph})=>(
                <div key={field}>
                  <label htmlFor={id} className="text-meta font-medium text-gray-500 block mb-0.5">{fl}</label>
                  <input id={id} type="number" step="any" value={(prop as any)[field]??''} placeholder={ph}
                    onChange={e=>handleConstraintChange(key,field,e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-label bg-white outline-none focus:border-blue-400 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none transition-colors duration-fast" />
                </div>
              ))}
            </div>
            <p className="text-meta text-gray-400">
              {prop.minimum!==undefined&&prop.maximum!==undefined&&prop.multipleOf!==undefined
                ?`Range: ${prop.minimum}–${prop.maximum}, step ${prop.multipleOf}`
                :'Set min, max, and step for slider controls'}
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderVarGroup = (gk:'basic'|'advanced', filterFn:(k:string)=>boolean) => {
    const items = Object.entries(parsedJsonSchema!.properties).filter(([k])=>filterFn(k));
    return (
      <div className={`border-2 rounded-lg transition-colors duration-fast ${dragOverGroup===gk?'border-blue-400 bg-blue-50/40':'border-dashed border-gray-200'}`}
        onDragOver={e=>handleDragOver(e,gk)} onDragLeave={handleDragLeave} onDrop={e=>handleDrop(e,gk)}>
        <div className={`px-4 py-2.5 border-b border-gray-200 flex items-center justify-between rounded-t-lg ${gk==='advanced'?'bg-gray-50':'bg-surface-subtle'}`}>
          <h4 className="text-label font-semibold text-gray-700 capitalize">{gk}</h4>
          <span className="text-meta text-gray-400 font-semibold">{items.length}</span>
        </div>
        <div className="p-3 flex flex-col gap-2 min-h-[60px]">
          {items.length===0
            ?<p className="text-meta text-gray-300 text-center py-4">Drop variables here</p>
            :items.map(([k,p])=>renderVariableCard(k,p,gk==='advanced'?'adv':'bas'))}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={handleCancel} aria-hidden="true" />
      <div className="w-[520px] flex-shrink-0 bg-white shadow-xl flex flex-col h-full">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">Workflow Details</h2>
          <button type="button" className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors duration-fast text-lg" onClick={handleCancel}>×</button>
        </div>

        {error && (
          <div className="mx-6 mt-3 px-4 py-3 bg-error-light text-error border border-error/30 rounded-md text-label flex-shrink-0">{error}</div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-6">
          {/* Metadata */}
          <section>
            <h3 className="text-label font-semibold text-gray-400 uppercase tracking-wider mb-4">Metadata</h3>
            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-label font-medium text-gray-700 mb-1">Display Name</label>
                <input type="text" value={displayName} aria-label="Display Name"
                  onChange={e=>setDisplayName(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-body bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast" />
              </div>
              <div>
                <label className="block text-label font-medium text-gray-700 mb-1">Description</label>
                <textarea value={description} rows={3} aria-label="Description"
                  onChange={e=>setDescription(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-body bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast resize-y" />
              </div>
              <div>
                <label className="block text-label font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
                <input type="text" value={tags} placeholder="e.g., image, upscale, enhancement"
                  onChange={e=>setTags(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-body bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast" />
              </div>
              {/* Icon */}
              <div>
                <label className="block text-label font-medium text-gray-700 mb-1">Icon</label>
                <p className="text-meta text-gray-400 mb-2">Upload a custom icon (PNG, JPG, SVG — max 500 KB)</p>
                <input ref={iconInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                  onChange={handleIconChange} className="hidden" aria-label="Upload icon" />
                {icon ? (
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-lg overflow-hidden border border-gray-200 flex items-center justify-center bg-surface-subtle">
                      {icon.startsWith('data:') || icon.startsWith('http://') || icon.startsWith('https://')
                        ? <img src={icon} alt="Workflow icon" className="w-full h-full object-cover" />
                        : <span className="text-2xl">{icon}</span>}
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={handleIconClick} disabled={iconUploading}
                        className="px-3 py-1.5 text-meta font-medium bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors duration-fast disabled:opacity-50">
                        {iconUploading ? 'Uploading…' : 'Change'}
                      </button>
                      <button type="button" onClick={handleIconRemove} disabled={iconUploading}
                        className="px-3 py-1.5 text-meta font-medium bg-error-light text-error border border-error/30 rounded-md hover:bg-red-100 transition-colors duration-fast disabled:opacity-50">
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-lg bg-surface-subtle border border-gray-200 flex items-center justify-center text-2xl">🎨</div>
                    <button type="button" onClick={handleIconClick} disabled={iconUploading}
                      className="px-4 py-2 text-label font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors duration-fast disabled:opacity-50">
                      {iconUploading ? 'Uploading…' : 'Upload Icon'}
                    </button>
                  </div>
                )}
              </div>
              {/* Read-only fields */}
              {[['Status', <span className={`px-2.5 py-0.5 rounded-full text-meta font-semibold capitalize ${metadata.status==='active'?'bg-success-light text-success-dark':'bg-gray-100 text-gray-500'}`}>{metadata.status}</span>],
                ['Version', metadata.version||'N/A'], ['Author', metadata.author||'N/A'],
                ['Created', new Date(metadata.createdAt).toLocaleString()],
                ['Updated', new Date(metadata.updatedAt).toLocaleString()],
              ].map(([l,v])=>(
                <div key={l as string}>
                  <label className="block text-label font-medium text-gray-700 mb-1">{l as string}</label>
                  <div className="px-3 py-2 bg-surface-subtle border border-gray-200 rounded-md text-label text-gray-700">{v as React.ReactNode}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Presentation */}
          <section>
            <h3 className="text-label font-semibold text-gray-400 uppercase tracking-wider mb-4">Presentation</h3>
            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-label font-medium text-gray-700 mb-1">JSON Schema</label>
                <p className="text-meta text-gray-400 mb-1">Defines the structure and validation rules for workflow parameters.</p>
                <textarea value={jsonSchema} rows={10} spellCheck={false} aria-label="JSON Schema"
                  onChange={e=>setJsonSchema(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-body font-mono text-meta bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast resize-y" />
              </div>
              <div>
                <label className="block text-label font-medium text-gray-700 mb-1">UI Schema</label>
                <p className="text-meta text-gray-400 mb-1">Defines the presentation and ordering of parameters in the UI.</p>
                <textarea value={uiSchema} rows={10} spellCheck={false} aria-label="UI Schema"
                  onChange={e=>setUiSchema(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-body font-mono text-meta bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast resize-y" />
              </div>
            </div>
          </section>

          {/* Package Info */}
          <section>
            <h3 className="text-label font-semibold text-gray-400 uppercase tracking-wider mb-4">Package Info</h3>
            <div className="flex flex-col gap-3">
              {[['Manifest ID', pkg.manifest.id], ['Manifest Name', pkg.manifest.name],
                ['Uploaded', new Date(pkg.uploadedAt).toLocaleString()], ['Uploaded By', pkg.uploadedBy],
              ].map(([l,v])=>(
                <div key={l}>
                  <label className="block text-label font-medium text-gray-700 mb-1">{l}</label>
                  <div className="px-3 py-2 bg-surface-subtle border border-gray-200 rounded-md text-label font-mono text-meta text-gray-700">{v}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Variables */}
          {parsedJsonSchema?.properties && (
            <section>
              <h3 className="text-label font-semibold text-gray-400 uppercase tracking-wider mb-1">
                Input Variables ({Object.keys(parsedJsonSchema.properties).length})
              </h3>
              <p className="text-meta text-gray-400 mb-4">Configure how each input parameter is displayed. Drag variables between Basic and Advanced sections.</p>
              <div className="flex flex-col gap-4">
                {renderVarGroup('basic',    k => !isFieldInAdvancedGroup(k))}
                {renderVarGroup('advanced', k =>  isFieldInAdvancedGroup(k))}
              </div>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex gap-3 flex-shrink-0">
          <button type="button" onClick={handleCancel} disabled={saving}
            className="px-4 py-2.5 bg-white border border-gray-300 text-label font-medium text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-fast disabled:opacity-50">
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={!changesDetected || saving}
            className="flex-1 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-label font-semibold rounded-md transition-colors duration-fast disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};


